import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Building2, Plus, Search, Users, DollarSign, AlertTriangle } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useUserRole } from '@/hooks/useUserRole';
import { useTenants } from '@/hooks/useTenants';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { TenantForm } from '@/components/tenants/TenantForm';
import { TenantUsersDialog } from '@/components/tenants/TenantUsersDialog';
import { SubscriptionBadge } from '@/components/tenants/SubscriptionBadge';
import { SubscriptionManagementDialog } from '@/components/tenants/SubscriptionManagementDialog';
import { SubscriptionSummaryCard } from '@/components/tenants/SubscriptionSummaryCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';

export default function Tenants() {
  const { isSuperAdmin, isLoading: roleLoading } = useUserRole();
  const { tenants, isLoading, userCounts } = useTenants();
  const { subscriptions, summaryStats } = useSubscriptions();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<any>(null);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [isUsersDialogOpen, setIsUsersDialogOpen] = useState(false);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);

  if (roleLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <h1 className="text-3xl font-bold">Tenant Management</h1>
          <TableSkeleton rows={5} columns={4} />
        </div>
      </DashboardLayout>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  const filteredTenants = tenants?.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUsers = Object.values(userCounts).reduce((sum, count) => sum + count, 0);
  const activeTenants = tenants?.filter(t => t.is_active).length || 0;

  // Get subscription for a tenant
  const getTenantSubscription = (tenantId: string) => {
    return subscriptions?.find(s => s.tenant_id === tenantId);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Tenant Management</h1>
        
        {/* Subscription Summary */}
        <SubscriptionSummaryCard
          totalMRR={summaryStats.totalMRR}
          totalARR={summaryStats.totalARR}
          activeCount={summaryStats.activeCount}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Tenants</CardDescription>
              <CardTitle className="text-3xl">{tenants?.length || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Active Tenants</CardDescription>
              <CardTitle className="text-3xl">{activeTenants}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Users</CardDescription>
              <CardTitle className="text-3xl">{totalUsers}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={() => {
            setEditingTenant(null);
            setIsFormOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Tenant
          </Button>
        </div>

        {/* Tenants Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Tenants</CardTitle>
            <CardDescription>
              Manage tenant accounts and view user statistics
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <TableSkeleton rows={5} columns={4} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No tenants found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTenants?.map((tenant) => {
                      const subscription = getTenantSubscription(tenant.id);
                      const userCount = userCounts[tenant.id] || 0;

                      return (
                        <TableRow key={tenant.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              {tenant.name}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {tenant.slug}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {userCount} users
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {subscription ? (
                              <div className="text-sm">
                                <div className="flex items-center gap-1">
                                  <SubscriptionBadge status={subscription.subscription_status} />
                                  {userCount > subscription.licensed_user_count && (
                                    <AlertTriangle className="h-3 w-3 text-destructive" />
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  ${(Number(subscription.base_monthly_fee) + (Number(subscription.per_user_monthly_fee) * subscription.licensed_user_count)).toFixed(2)}/mo
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {subscription.licensed_user_count} licenses ({userCount} active)
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">No subscription</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={tenant.is_active ? 'default' : 'secondary'}>
                              {tenant.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {format(new Date(tenant.created_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedTenant(tenant);
                                  setIsSubscriptionDialogOpen(true);
                                }}
                              >
                                <DollarSign className="h-4 w-4 mr-1" />
                                Subscription
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedTenant(tenant);
                                  setIsUsersDialogOpen(true);
                                }}
                              >
                                <Users className="h-4 w-4 mr-1" />
                                Users
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingTenant(tenant);
                                  setIsFormOpen(true);
                                }}
                              >
                                Edit
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <TenantForm
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          tenant={editingTenant}
        />

        <TenantUsersDialog
          open={isUsersDialogOpen}
          onOpenChange={setIsUsersDialogOpen}
          tenant={selectedTenant}
          userCount={selectedTenant ? userCounts[selectedTenant.id] || 0 : 0}
        />

        {selectedTenant && (
          <SubscriptionManagementDialog
            open={isSubscriptionDialogOpen}
            onOpenChange={setIsSubscriptionDialogOpen}
            tenantId={selectedTenant.id}
            tenantName={selectedTenant.name}
            userCount={userCounts[selectedTenant.id] || 0}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
